/**
 * Markdown 渲染服务
 * 基于 marked 库实现，支持代码高亮、数学公式和 Mermaid 图表
 */
import { marked } from 'marked'
import katex from 'katex'
import 'katex/dist/katex.min.css'
import mermaid from 'mermaid'
import i18n from '../../../i18n/config'

// --- 类型定义 ---

interface RenderContext {
  math: Map<string, { formula: string; displayMode: boolean }>
  mermaid: Map<string, { id: string; code: string; raw: string }>
  echarts: Map<string, { id: string; option: string }>
  counters: {
    math: number
    mermaid: number
    echarts: number
  }
  // 唯一前缀，避免多实例 ID 冲突
  prefix: string
}

// --- 初始化逻辑 ---

export const initializeMermaid = (isDark: boolean = false) => {
  if (typeof window === 'undefined') return

  mermaid.initialize({
    startOnLoad: false,
    securityLevel: 'loose',
    fontFamily: 'inherit',
    theme: isDark ? 'dark' : 'default',
    themeVariables: isDark ? {
      primaryColor: '#3b82f6',
      primaryTextColor: '#f3f4f6',
      primaryBorderColor: '#2563eb',
      lineColor: '#9ca3af',
      secondaryColor: '#1e293b',
      tertiaryColor: '#111827'
    } : undefined
  })
}

// --- 辅助工具 ---

const createRenderContext = (): RenderContext => ({
  math: new Map(),
  mermaid: new Map(),
  echarts: new Map(),
  counters: { math: 0, mermaid: 0, echarts: 0 },
  // 使用时间戳+随机数生成唯一前缀，避免多实例渲染时 ID 冲突
  prefix: `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`
})

const escapeHtml = (text: string): string => {
  if (typeof document === 'undefined') return text
  const div = document.createElement('div')
  div.textContent = text
  return div.innerHTML
}

const safeBtoa = (str: string): string => {
  try {
    return window.btoa(encodeURIComponent(str))
  } catch (e) {
    console.error('Base64 encoding failed:', e)
    return ''
  }
}

// --- HTML 模板 ---

const getActionButtonsHtml = (hasZoom = false, hasDownload = false, hasLiveEdit = false, hasInternalEdit = false, hasTableExport = false) => {
  const downloadBtn = hasDownload ? `
    <div class="preview-dropdown-wrapper" data-action="download-menu">
      <button class="preview-action-button" title="${i18n.t('preview.exportImage')}">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2"></path>
          <polyline points="7 11 12 16 17 11"></polyline>
          <line x1="12" y1="4" x2="12" y2="16"></line>
        </svg>
      </button>
      <div class="preview-dropdown-menu">
        <button class="preview-dropdown-item" data-format="svg">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 15v4c0 1.1.9 2 2 2h14a2 2 0 0 0 2-2v-4M17 9l-5 5-5-5M12 12.8V2.5"/></svg>
          <span>SVG</span>
        </button>
        <button class="preview-dropdown-item" data-format="png">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
          <span>PNG</span>
        </button>
        <button class="preview-dropdown-item" data-format="jpeg">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>
          <span>JPEG</span>
        </button>
      </div>
    </div>
  ` : ''

  const tableExportBtn = hasTableExport ? `
    <div class="preview-dropdown-wrapper" data-action="download-menu">
      <button class="preview-action-button" title="${i18n.t('preview.exportTable')}">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
          <polyline points="7 10 12 15 17 10"></polyline>
          <line x1="12" y1="15" x2="12" y2="3"></line>
        </svg>
      </button>
      <div class="preview-dropdown-menu">
        <button class="preview-dropdown-item" data-format="excel">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 3h18v18H3zM3 9h18M3 15h18M9 3v18M15 3v18"/></svg>
          <span>Excel</span>
        </button>
        <button class="preview-dropdown-item" data-format="csv">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><path d="M8 13h8"></path><path d="M8 17h8"></path><path d="M10 9h4"></path></svg>
          <span>CSV</span>
        </button>
      </div>
    </div>
  ` : ''

  const liveEditBtn = hasLiveEdit ? `
    <div class="preview-dropdown-wrapper" data-action="edit-menu">
      <button class="preview-action-button" title="${i18n.t('editor.edit')}">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
        </svg>
      </button>
      <div class="preview-dropdown-menu">
        <button class="preview-dropdown-item" data-action="internal-edit">
          <span>${i18n.t('preview.internalEdit')}</span>
        </button>
        <button class="preview-dropdown-item" data-action="live-edit">
          <span>${i18n.t('preview.liveEditor')}</span>
        </button>
      </div>
    </div>
  ` : ''

  const internalEditBtn = hasInternalEdit && !hasLiveEdit ? `
    <button class="preview-action-button" data-action="internal-edit" title="${i18n.t('editor.edit')}">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
      </svg>
    </button>
  ` : ''

  const zoomBtn = hasZoom ? `
    <button class="preview-action-button" data-action="zoom" title="${i18n.t('preview.zoom')}">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M15 3h6v6"></path>
        <path d="M9 21H3v-6"></path>
        <path d="M21 3l-7 7"></path>
        <path d="M3 21l7-7"></path>
      </svg>
    </button>
  ` : ''

  const copyBtn = `
    <button class="preview-action-button" data-action="copy" title="${i18n.t('preview.copy')}">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
      </svg>
    </button>
  `

  return `
    <div class="preview-actions">
      ${liveEditBtn}
      ${internalEditBtn}
      ${downloadBtn}
      ${tableExportBtn}
      ${zoomBtn}
      ${copyBtn}
    </div>
  `
}

// --- 解析器与恢复器 ---

const processMath = (content: string, ctx: RenderContext): string => {
  let processed = content
  // 块级公式
  processed = processed.replace(/\$\$([\s\S]*?)\$\$/g, (_, formula) => {
    const placeholder = `%%MATH_BLOCK_${ctx.counters.math++}%%`
    ctx.math.set(placeholder, { formula: formula.trim(), displayMode: true })
    return `\n\n${placeholder}\n\n`
  })
  // 行内公式
  processed = processed.replace(/\$([^\$\n]+?)\$/g, (_, formula) => {
    const placeholder = `%%MATH_INLINE_${ctx.counters.math++}%%`
    ctx.math.set(placeholder, { formula: formula.trim(), displayMode: false })
    return placeholder
  })
  return processed
}

const processMermaid = (content: string, ctx: RenderContext): string => {
  return content.replace(/```mermaid\n([\s\S]*?)```/g, (match, code) => {
    const placeholder = `%%MERMAID_${ctx.counters.mermaid++}%%`
    // 使用唯一前缀避免多实例渲染时 ID 冲突
    ctx.mermaid.set(placeholder, { id: `mermaid-${ctx.prefix}-${ctx.counters.mermaid - 1}`, code: code.trim(), raw: match })
    return placeholder
  })
}

const processEcharts = (content: string, ctx: RenderContext): string => {
  return content.replace(/```echarts\s*\n([\s\S]*?)```/g, (_, code) => {
    const placeholder = `%%ECHARTS_${ctx.counters.echarts++}%%`
    // 使用唯一前缀避免多实例渲染时 ID 冲突
    ctx.echarts.set(placeholder, { id: `echarts-${ctx.prefix}-${ctx.counters.echarts - 1}`, option: code.trim() })
    return placeholder
  })
}

const restoreMath = (html: string, ctx: RenderContext): string => {
  let result = html
  ctx.math.forEach((data, placeholder) => {
    try {
      const rendered = katex.renderToString(data.formula, {
        displayMode: data.displayMode,
        throwOnError: false,
        strict: false,
      })
      const wrapper = data.displayMode
        ? `<div class="math-block" style="text-align: center; margin: 1em 0; overflow-x: auto;">${rendered}</div>`
        : `<span class="math-inline">${rendered}</span>`
      result = result.replace(new RegExp(placeholder, 'g'), wrapper)
    } catch (e) {
      result = result.replace(new RegExp(placeholder, 'g'), `<span class="text-red-500">${data.formula}</span>`)
    }
  })
  return result
}

const restoreMermaid = async (html: string, ctx: RenderContext): Promise<string> => {
  let result = html
  for (const [placeholder, data] of ctx.mermaid) {
    try {
      const { svg } = await mermaid.render(data.id, data.code)
      const encodedRaw = safeBtoa(data.raw)
      const container = `
        <div class="copy-button-wrapper mermaid-wrapper">
          <div class="mermaid-container" data-raw-markdown="${encodedRaw}">${svg}</div>
          ${getActionButtonsHtml(true, true, true)}
        </div>
      `
      result = result.replace(new RegExp(placeholder, 'g'), container)
    } catch (e) {
      const encodedRaw = safeBtoa(data.raw)
      const fallback = `
        <div class="copy-button-wrapper mermaid-wrapper">
          <pre class="language-mermaid" data-raw-markdown="${encodedRaw}"><code>${escapeHtml(data.code)}</code></pre>
          ${getActionButtonsHtml(false, false, true)}
        </div>
      `
      result = result.replace(new RegExp(placeholder, 'g'), fallback)
    }
  }
  return result
}

const restoreEcharts = (html: string, ctx: RenderContext): string => {
  let result = html
  ctx.echarts.forEach((data, placeholder) => {
    const encodedRaw = safeBtoa(`\`\`\`echarts\n${data.option}\n\`\`\``)
    const encodedOption = safeBtoa(data.option)
    const container = `
      <div class="copy-button-wrapper echarts-wrapper">
        <div class="echarts-container" data-echarts-id="${data.id}" data-echarts-option="${encodedOption}" data-raw-markdown="${encodedRaw}"></div>
        ${getActionButtonsHtml(true, false, true)}
      </div>
    `
    result = result.replace(new RegExp(placeholder, 'g'), container)
  })
  return result
}

// --- Marked 配置 ---

marked.setOptions({ breaks: true, gfm: true })

const customRenderer = {
  code({ text, lang, raw }: { text: string; lang?: string; raw: string }): string {
    const validLanguage = lang || 'plaintext'
    const encodedRaw = safeBtoa(raw)
    return `
      <div class="copy-button-wrapper">
        ${getActionButtonsHtml(false, false, false, true)}
        <pre data-raw-markdown="${encodedRaw}"><code class="language-${validLanguage}">${escapeHtml(text)}</code></pre>
      </div>
    `
  },
  table(token: any): string {
    let headerHtml = '<thead><tr>'
    token.header.forEach((cell: any) => {
      headerHtml += `<th>${marked.parseInline(cell.text)}</th>`
    })
    headerHtml += '</tr></thead>'

    let bodyHtml = '<tbody>'
    token.rows.forEach((row: any[]) => {
      bodyHtml += '<tr>'
      row.forEach((cell: any) => {
        bodyHtml += `<td>${marked.parseInline(cell.text)}</td>`
      })
      bodyHtml += '</tr>'
    })
    bodyHtml += '</tbody>'

    const encodedRaw = safeBtoa(token.raw)
    return `
      <div class="copy-button-wrapper">
        ${getActionButtonsHtml(false, false, false, true, true)}
        <table data-raw-markdown="${encodedRaw}">${headerHtml}${bodyHtml}</table>
      </div>
    `
  },
  link({ href, title, text }: { href: string; title?: string | null; text: string }): string {
    const titleAttr = title ? ` title="${title}"` : ''
    return `<a href="${href}"${titleAttr} target="_blank" rel="noopener noreferrer">${text}</a>`
  }
}

marked.use({ renderer: customRenderer })

// --- 公开 API ---

export function preprocessMarkdown(markdown: string): string {
  if (!markdown) return ''
  return markdown
    .replace(/\\\[([\s\S]*?)\\\]/g, '$$$$$1$$$$')
    .replace(/\\\(([\s\S]*?)\\\)/g, '$$$1$$')
}

export async function renderMarkdownFull(markdown: string, isDark: boolean = false): Promise<string> {
  if (!markdown) return ''
  
  const ctx = createRenderContext()
  initializeMermaid(isDark)

  try {
    const preprocessed = preprocessMarkdown(markdown)
    const withMath = processMath(preprocessed, ctx)
    const withMermaid = processMermaid(withMath, ctx)
    const withEcharts = processEcharts(withMermaid, ctx)
    
    const rawHtml = marked.parse(withEcharts) as string
    
    const htmlWithMath = restoreMath(rawHtml, ctx)
    const htmlWithMermaid = await restoreMermaid(htmlWithMath, ctx)
    return restoreEcharts(htmlWithMermaid, ctx)
  } catch (error) {
    console.error('Render failed:', error)
    return `<div class="render-error">Render Error: ${error instanceof Error ? error.message : 'Unknown'}</div>`
  }
}
